// ─────────────────────────────────────────────────────────────────────────────
// SortBeat Daily — Beat Visuals
// Pure worklet-compatible functions for the beat-grid glow pulse.
// No React imports — safe to unit test without a device.
//
// Designed by UI Designer agent (2026-06-29):
// - Exponential decay curve: feels like a physical drum transient (instant
//   attack, trailing resonance) — not sine-wave "breathing"
// - Chain-count scaling: game "wakes up" on first chain (0.15→0.40 jump),
//   reaches climax at 3+ chains (0.90)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the alpha opacity for the beat-grid glow at a given beat phase.
 *
 * Uses exponential decay: maxOpacity at phase 0 (beat just hit), decaying
 * to baseline by ~phase 0.5. Half-life at 18% of the beat interval.
 *
 * @param beatPhase  0.0 at beat boundary, 1.0 at next beat (from inferBeatPhase)
 * @param maxOpacity Ceiling opacity — varies by chain count (see chainMaxOpacity)
 */
export function beatPulseOpacity(beatPhase: number, maxOpacity: number): number {
  'worklet';
  const baseline = 0.15;
  if (maxOpacity <= baseline) return baseline;
  // k = ln(2) / 0.18 ≈ 3.85 — half-life at 18% of beat interval
  return baseline + (maxOpacity - baseline) * Math.exp(-3.85 * beatPhase);
}

/**
 * Returns the glow opacity ceiling based on the number of pending chain
 * explosions. Scales from a barely-visible baseline tick (no chains) up to
 * a prominent flash (3+ chains clearing simultaneously).
 *
 * @param chainCount  Number of chains currently pending explosion
 */
export function chainMaxOpacity(chainCount: number): number {
  'worklet';
  // BUG FIX (founder playtest, 2026-07-07): baseline was 0.15, explicitly
  // commented "no visible pulse" — but this pulse's entire documented
  // purpose (docs/viral-ux-changes.md Change 2) is to make the rhythm
  // legible at a glance, even on a muted device. A near-invisible baseline
  // directly undermined that goal — players correctly reported "there's no
  // beat bar anymore" despite it technically always being there. Raised
  // enough to actually read as a persistent pulse without competing with
  // the escalation tiers below it.
  if (chainCount <= 0) return 0.32;
  if (chainCount === 1) return 0.40;
  if (chainCount === 2) return 0.65;
  return 0.90; // 3+
}
